import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL, fetchJson } from './api';

function getAuthHeader() {
    try {
        const token = String(localStorage.getItem('monsurv_auth_token') || '').trim();
        return token ? { Authorization: `Bearer ${token}` } : {};
    } catch {
        return {};
    }
}

function hasAuthToken() {
    try {
        return Boolean(String(localStorage.getItem('monsurv_auth_token') || '').trim());
    } catch {
        return false;
    }
}

function riskBadgeColor(signal) {
    const s = String(signal || '').toUpperCase();
    if (s === 'RED') return { bg: '#fee2e2', fg: '#991b1b', border: '#fecaca' };
    if (s === 'AMBER') return { bg: '#ffedd5', fg: '#9a3412', border: '#fed7aa' };
    return { bg: '#dcfce7', fg: '#166534', border: '#bbf7d0' };
}

export default function RecommendedSurveyorsPanel({ projectReference, currentLeadSurveyor, onAssigned, allowedNames }) {
    const ref = String(projectReference || '').trim();
    const chosenName = String(currentLeadSurveyor || '').trim();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [data, setData] = useState(null);
    const [assigningId, setAssigningId] = useState('');
    const [message, setMessage] = useState('');

    const [chosenLoading, setChosenLoading] = useState(false);
    const [chosenError, setChosenError] = useState('');
    const [chosenMetrics, setChosenMetrics] = useState(null);

    const tokenPresent = hasAuthToken();

    const allowSet = useMemo(() => {
        if (allowedNames instanceof Set) return allowedNames;
        if (Array.isArray(allowedNames)) {
            return new Set(allowedNames.map((x) => String(x || '').trim().toLowerCase()).filter(Boolean));
        }
        return null;
    }, [allowedNames]);

    async function loadChosenMetrics() {
        if (!chosenName) return;
        setChosenLoading(true);
        setChosenError('');
        setChosenMetrics(null);
        try {
            const json = await fetchJson(
                `${API_BASE_URL}/api/workload/overview?horizon_days=30`,
                { method: 'GET', headers: { ...getAuthHeader() } }
            );

            const rows = Array.isArray(json?.surveyors) ? json.surveyors : [];
            const target = chosenName.toLowerCase();
            const found = rows.find((r) => String(r?.name || '').trim().toLowerCase() === target) || null;
            setChosenMetrics(found);

            if (!found) {
                setChosenError('Metrics unavailable for the chosen surveyor.');
            }
        } catch (e) {
            setChosenError(e?.message || 'Failed to load chosen surveyor metrics');
        } finally {
            setChosenLoading(false);
        }
    }

    async function load() {
        if (!ref) return;
        setLoading(true);
        setError('');
        setMessage('');
        try {
            const json = await fetchJson(
                `${API_BASE_URL}/api/workload/recommendations?project_reference=${encodeURIComponent(ref)}`,
                { method: 'GET', headers: { ...getAuthHeader() } }
            );
            setData(json || null);
        } catch (e) {
            setData(null);
            setError(e?.message || 'Failed to load recommendations');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (!ref) return;
        if (!tokenPresent) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref, tokenPresent]);

    useEffect(() => {
        if (!tokenPresent) return;
        if (!chosenName) {
            setChosenError('');
            setChosenMetrics(null);
            setChosenLoading(false);
            return;
        }

        loadChosenMetrics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tokenPresent, chosenName]);

    async function assign(reco) {
        const surveyorId = String(reco?.surveyor_id || '').trim();
        if (!ref || !surveyorId) return;

        setAssigningId(surveyorId);
        setError('');
        setMessage('');

        try {
            const out = await fetchJson(`${API_BASE_URL}/api/workload/assign`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
                body: JSON.stringify({ project_reference: ref, surveyor_id: surveyorId, is_primary: true })
            });

            setMessage('Assigned.');
            if (onAssigned) onAssigned(out);

            await load();
        } catch (e) {
            setError(e?.message || 'Failed to assign');
        } finally {
            setAssigningId('');
        }
    }

    if (!ref) return null;

    if (!tokenPresent) {
        return (
            <div style={{ marginTop: 10, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fafafa' }}>
                <div style={{ fontWeight: 650 }}>Surveyor Metrics</div>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
                    Sign in to view workload-based recommendations.
                </div>
            </div>
        );
    }

    const signal = String(data?.project_risk_signal || 'GREEN').toUpperCase();
    const badge = riskBadgeColor(signal);
    const recsRaw = Array.isArray(data?.recommendations) ? data.recommendations : [];
    const recs = allowSet
        ? recsRaw.filter((r) => allowSet.has(String(r?.name || '').trim().toLowerCase()))
        : recsRaw;

    return (
        <div style={{ marginTop: 10, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ fontWeight: 650 }}>Surveyor Metrics</div>
                    <div style={{ fontSize: 12, padding: '2px 8px', borderRadius: 999, background: badge.bg, color: badge.fg, border: `1px solid ${badge.border}` }}>
                        Risk: {signal}
                    </div>
                </div>
                <button type="button" onClick={load} disabled={loading || Boolean(assigningId)}>
                    {loading ? 'Loading…' : 'Refresh'}
                </button>
            </div>

            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>
                Uses utilisation, deadline congestion, and (if RED) seniority.
            </div>

            <div style={{ marginTop: 10, padding: 10, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fafafa' }}>
                <div style={{ fontSize: 12, fontWeight: 650, color: '#111827' }}>
                    Chosen surveyor
                </div>
                {!chosenName ? (
                    <div style={{ marginTop: 4, fontSize: 13, color: '#6b7280' }}>
                        Unassigned.
                    </div>
                ) : chosenLoading ? (
                    <div style={{ marginTop: 4, fontSize: 13, color: '#6b7280' }}>
                        Loading metrics for {chosenName}…
                    </div>
                ) : chosenMetrics ? (
                    <div style={{ marginTop: 4 }}>
                        <div style={{ fontWeight: 650 }}>{chosenMetrics.name}</div>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>
                            {chosenMetrics.role_level || '—'} • Util {chosenMetrics.utilisation_pct ?? '—'}% • Active {chosenMetrics.active_project_count ?? 0} / {chosenMetrics.max_concurrent_projects ?? '—'}
                        </div>
                        <div style={{ marginTop: 2, fontSize: 12, color: '#6b7280' }}>
                            Points {chosenMetrics.total_points ?? 0} / {chosenMetrics.weekly_capacity_points ?? '—'} • Deadlines (7d) {chosenMetrics.open_deadlines_7d ?? 0} • Booked ({chosenMetrics.bookings_horizon_days ?? 14}d) {chosenMetrics.booked_slots_14d ?? 0}
                        </div>
                        <div style={{ marginTop: 2, fontSize: 12, color: '#6b7280' }}>
                            RED exposure {chosenMetrics.risk_exposure_red_count ?? 0}
                            {chosenMetrics.nearest_due_days_min !== null && chosenMetrics.nearest_due_days_min !== undefined
                                ? ` • Nearest due ${chosenMetrics.nearest_due_days_min}d`
                                : ''}
                            {chosenMetrics.active_risk_red_count !== null && chosenMetrics.active_risk_red_count !== undefined
                                ? ` • Risk mix R${chosenMetrics.active_risk_red_count ?? 0}/A${chosenMetrics.active_risk_amber_count ?? 0}/G${chosenMetrics.active_risk_green_count ?? 0}`
                                : ''}
                        </div>
                    </div>
                ) : (
                    <div style={{ marginTop: 4, fontSize: 13, color: '#6b7280' }}>
                        {chosenError ? chosenError : `No metrics available for ${chosenName}.`}
                    </div>
                )}
            </div>

            {error ? (
                <div style={{ marginTop: 10, padding: 10, border: '1px solid #fecaca', background: '#fff1f2', color: '#991b1b', borderRadius: 8 }}>
                    {error}
                </div>
            ) : null}

            {message ? (
                <div style={{ marginTop: 10, padding: 10, border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#166534', borderRadius: 8 }}>
                    {message}
                </div>
            ) : null}

            {recs.length === 0 ? (
                <div style={{ marginTop: 10, fontSize: 13, color: '#6b7280' }}>
                    {loading ? 'Loading…' : 'No recommendations available (all surveyors at max concurrency).'}
                </div>
            ) : (
                <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
                    {recs.map((r) => {
                        const isCurrent = currentLeadSurveyor && String(currentLeadSurveyor).trim() && String(r?.name || '').trim() === String(currentLeadSurveyor).trim();
                        const reasons = Array.isArray(r?.reasons) ? r.reasons : [];
                        return (
                            <div key={r.surveyor_id} style={{ border: '1px solid #f3f4f6', borderRadius: 8, padding: 10, background: isCurrent ? '#f8fafc' : '#fff' }}>
                                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
                                    <div>
                                        <div style={{ fontWeight: 650 }}>{r.name}</div>
                                        <div style={{ fontSize: 12, color: '#6b7280' }}>
                                            {r.role_level || '—'} • Util {r.utilisation_pct ?? '—'}% • Active {r.active_project_count ?? 0}{r.max_concurrent_projects ? ` / ${r.max_concurrent_projects}` : ''}
                                        </div>
                                        <div style={{ fontSize: 12, color: '#6b7280' }}>
                                            Deadlines (7d) {r.open_deadlines_7d ?? '—'} • RED exposure {r.risk_exposure_red_count ?? '—'}
                                            {r.nearest_due_days_min !== null && r.nearest_due_days_min !== undefined ? ` • Nearest due ${r.nearest_due_days_min}d` : ''}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => assign(r)}
                                        disabled={Boolean(assigningId) || isCurrent}
                                        title={isCurrent ? 'Already assigned as lead surveyor' : 'Assign as lead surveyor'}
                                    >
                                        {assigningId === r.surveyor_id ? 'Assigning…' : (isCurrent ? 'Assigned' : 'Assign')}
                                    </button>
                                </div>

                                {reasons.length ? (
                                    <ul style={{ margin: '8px 0 0 16px', padding: 0, fontSize: 12, color: '#374151' }}>
                                        {reasons.map((x, idx) => (
                                            <li key={`${r.surveyor_id}-${idx}`}>{x}</li>
                                        ))}
                                    </ul>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
